import { useAtom } from 'jotai'
import { atomWithStorage, createJSONStorage } from 'jotai/utils'
import posthog from 'posthog-js'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StorageKeys } from '~/atoms/StorageKeys'
import { useUserState } from '~/atoms/UserState'
import { useCheckRole } from '~/hooks/useCheckRole'
import { useInstalledAppPartner } from '~/hooks/useCurrentPartner'
import { observe } from '~/services/observability/observe'
import { AnalyticsNamespace, SharedAnalyticsEvents } from '~/types/Analytics'
import {
  Announcement,
  AnnouncementDismissMode,
  AnnouncementIssue,
  AnnouncementSeverity,
  compareAnnouncements,
  getAnnouncementStorageKey,
  isAnnouncementEligible,
  parseAnnouncementPayload,
  resolveLocalizedText,
} from '~/utils/announcements'
import { getApiEnvironment, getAppVersion, isElectron } from '~/utils/common'
import { resolvePartnerId } from '~/utils/partner'

import { FeatureFlag } from '@mangovoice/constants'
import { isPosthogReady, onPosthogReady } from '@mangovoice/core'

export const ANNOUNCEMENT_RELOAD_INTERVAL_MS = 30 * 60 * 1000
export const ANNOUNCEMENT_CLOCK_INTERVAL_MS = 60 * 1000
// Checked every minute, so a reload always happens within the interval.
const RELOAD_STALE_AFTER_MS =
  ANNOUNCEMENT_RELOAD_INTERVAL_MS - ANNOUNCEMENT_CLOCK_INTERVAL_MS

export type AnnouncementEvent = 'shown' | 'dismissed' | 'cta_clicked'

const ANALYTICS_EVENTS: Record<AnnouncementEvent, SharedAnalyticsEvents> = {
  shown: SharedAnalyticsEvents.AnnouncementShown,
  dismissed: SharedAnalyticsEvents.AnnouncementDismissed,
  cta_clicked: SharedAnalyticsEvents.AnnouncementCtaClicked,
}

interface AnnouncementRecord {
  dismissed?: boolean
  collapsed?: boolean
  tracked?: AnnouncementEvent[]
}

type AnnouncementRecords = Record<string, AnnouncementRecord>

// Read on init so a dismissed banner does not flash before storage loads.
const announcementRecordsAtom = atomWithStorage<AnnouncementRecords>(
  StorageKeys.Announcements,
  {},
  createJSONStorage<AnnouncementRecords>(),
  { getOnInit: true },
)

export interface AnnouncementView {
  /** `userId:id@version`. */
  key: string
  id: string
  version: number
  severity: AnnouncementSeverity
  dismissMode: AnnouncementDismissMode
  title: string
  body?: string
  cta?: { label: string; url: string }
  isCollapsed: boolean
}

const MAX_REPORTED_ISSUES = 50

const hashString = (value: string): string => {
  let hash = 5381
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0
  }
  return (hash >>> 0).toString(36)
}

export const getAnnouncementIssueSignature = (issue: AnnouncementIssue) =>
  hashString(JSON.stringify(issue))

const readReportedIssues = (): string[] => {
  try {
    const stored = JSON.parse(
      localStorage.getItem(StorageKeys.AnnouncementIssues) ?? '[]',
    )
    return Array.isArray(stored) ? stored : []
  } catch {
    return []
  }
}

const sessionReportedIssues = new Set<string>()

// Each client reports an issue once, across sessions, so one bad entry does
// not send a warning on every app load.
const reportIssues = (issues: AnnouncementIssue[]) => {
  if (issues.length === 0) return
  const reported = readReportedIssues()
  const reportedCount = reported.length

  issues.forEach((issue) => {
    const signature = getAnnouncementIssueSignature(issue)
    if (sessionReportedIssues.has(signature) || reported.includes(signature)) {
      return
    }
    sessionReportedIssues.add(signature)
    reported.push(signature)

    observe.error(new Error(`Announcement payload: ${issue.reason}`), {
      level: 'warning',
      tags: { announcement_id: issue.id, announcement_issue: issue.reason },
      extra: { index: issue.index, problems: issue.problems },
      fingerprint: ['announcement-payload', issue.reason, issue.id ?? ''],
    })
  })

  if (reported.length === reportedCount) return
  try {
    localStorage.setItem(
      StorageKeys.AnnouncementIssues,
      JSON.stringify(reported.slice(-MAX_REPORTED_ISSUES)),
    )
  } catch {
    // Without storage, the in-memory set still dedupes this session.
  }
}

interface AnnouncementPayloadState {
  announcements: Announcement[]
  /** True once a payload with a valid envelope has loaded. */
  hasValidPayload: boolean
}

const EMPTY_PAYLOAD: AnnouncementPayloadState = {
  announcements: [],
  hasValidPayload: false,
}

// Dev-only stand-in for the PostHog flag payload (same JSON shape). The DEV
// read dead-code-eliminates out of prod builds. Ignored under Vitest, which
// loads the same env files, so a local override can't change test results.
const devAnnouncementsOverride: string | undefined =
  import.meta.env.DEV && import.meta.env.MODE !== 'test'
    ? import.meta.env.VITE_ANNOUNCEMENTS_OVERRIDE || undefined
    : undefined

export const readAnnouncementsOverride = (
  override: string | undefined,
): AnnouncementPayloadState | undefined => {
  if (override === undefined) return undefined
  const parsed = parseAnnouncementPayload(override)
  if (parsed.issues.length > 0) {
    console.warn('VITE_ANNOUNCEMENTS_OVERRIDE issues', parsed.issues)
  }
  // Not a real payload, so stored records for live announcements are kept.
  return { announcements: parsed.announcements, hasValidPayload: false }
}

/**
 * Valid announcements from the PostHog flag payload. A failed flag reload
 * keeps the last valid list.
 */
const useAnnouncementPayload = (): AnnouncementPayloadState => {
  const [payload, setPayload] = useState(
    () => readAnnouncementsOverride(devAnnouncementsOverride) ?? EMPTY_PAYLOAD,
  )

  useEffect(() => {
    if (devAnnouncementsOverride !== undefined) return

    let unsubscribeFlags: (() => void) | undefined
    let lastReloadAt = Date.now()

    const unsubscribeReady = onPosthogReady(() => {
      lastReloadAt = Date.now()
      unsubscribeFlags = posthog.onFeatureFlags(
        (_flags, _variants, context) => {
          if (context?.errorsLoading) return
          const result = posthog.getFeatureFlagResult(
            FeatureFlag.Announcements,
            {
              send_event: false,
            },
          )
          const raw = result?.enabled ? result.payload : undefined
          const parsed = parseAnnouncementPayload(raw)
          reportIssues(parsed.issues)
          setPayload({
            announcements: parsed.announcements,
            hasValidPayload:
              raw != null &&
              !parsed.issues.some(
                (issue) => issue.reason === 'invalid_envelope',
              ),
          })
        },
      )
    })

    const reload = () => {
      if (!isPosthogReady()) return
      lastReloadAt = Date.now()
      posthog.reloadFeatureFlags()
    }
    // Hidden tabs and minimized windows skip reloads (each one is a billed
    // flag request) and catch up when they are shown or focused again.
    const reloadIfStale = () => {
      if (
        document.visibilityState !== 'hidden' &&
        Date.now() - lastReloadAt >= RELOAD_STALE_AFTER_MS
      ) {
        reload()
      }
    }

    const intervalId = window.setInterval(
      reloadIfStale,
      ANNOUNCEMENT_CLOCK_INTERVAL_MS,
    )
    window.addEventListener('focus', reloadIfStale)
    document.addEventListener('visibilitychange', reloadIfStale)

    return () => {
      unsubscribeReady()
      unsubscribeFlags?.()
      window.clearInterval(intervalId)
      window.removeEventListener('focus', reloadIfStale)
      document.removeEventListener('visibilitychange', reloadIfStale)
    }
  }, [])

  return payload
}

const useMinuteClock = (): Date => {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const intervalId = window.setInterval(
      () => setNow(new Date()),
      ANNOUNCEMENT_CLOCK_INTERVAL_MS,
    )
    return () => window.clearInterval(intervalId)
  }, [])
  return now
}

const useDesktopAppVersion = (): string | undefined => {
  const [version, setVersion] = useState<string>()
  useEffect(() => {
    if (!isElectron) return
    let cancelled = false
    void getAppVersion().then((value) => {
      if (!cancelled) setVersion(value)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return version
}

/**
 * Remote announcements for the signed-in user: every eligible message, newest
 * first, plus the actions the banner needs. Mount it once.
 */
export const useAnnouncements = () => {
  const { announcements, hasValidPayload } = useAnnouncementPayload()
  const now = useMinuteClock()
  const appVersion = useDesktopAppVersion()
  const hasRole = useCheckRole()
  const installedAppPartner = useInstalledAppPartner()
  const { sessionUser, pbx } = useUserState({ canAutoRefresh: false })
  const partner = resolvePartnerId(installedAppPartner, pbx?.partner?.key)
  // A partner desktop build reports its partner asynchronously; until then the
  // PBX key can name the wrong brand.
  const isPartnerPending = installedAppPartner === null
  const companyCountry = pbx?.company_country
  const userId = sessionUser?.id
  const { i18n } = useTranslation()
  const [storedRecords, setRecords] = useAtom(announcementRecordsAtom)
  const records = useMemo<AnnouncementRecords>(
    () =>
      storedRecords && typeof storedRecords === 'object' ? storedRecords : {},
    [storedRecords],
  )
  const trackedThisSession = useRef(new Set<string>())

  const views = useMemo<AnnouncementView[]>(() => {
    if (userId == null) return []

    const context = {
      now,
      hasRole,
      region: getApiEnvironment(),
      platform: isElectron ? 'desktop' : 'web',
      partner,
      companyCountry,
      appVersion,
    } as const

    return announcements
      .filter(
        (announcement) =>
          !(isPartnerPending && announcement.audience?.partners) &&
          isAnnouncementEligible(announcement, context),
      )
      .map((announcement) => ({
        announcement,
        key: getAnnouncementStorageKey(userId, announcement),
      }))
      .filter(
        ({ announcement, key }) =>
          !(announcement.dismissMode === 'dismiss' && records[key]?.dismissed),
      )
      .sort((a, b) => compareAnnouncements(a.announcement, b.announcement))
      .map(({ announcement, key }): AnnouncementView => ({
        key,
        id: announcement.id,
        version: announcement.version,
        severity: announcement.severity,
        dismissMode: announcement.dismissMode,
        title: resolveLocalizedText(announcement.title, i18n.language),
        body:
          announcement.body &&
          resolveLocalizedText(announcement.body, i18n.language),
        cta: announcement.cta && {
          label: resolveLocalizedText(announcement.cta.label, i18n.language),
          url: announcement.cta.url,
        },
        isCollapsed:
          announcement.dismissMode === 'collapse' && !!records[key]?.collapsed,
      }))
  }, [
    announcements,
    now,
    hasRole,
    partner,
    companyCountry,
    isPartnerPending,
    appVersion,
    userId,
    records,
    i18n.language,
  ])

  // Drops this user's records for messages and versions that the payload no
  // longer has, so stored state does not grow without limit.
  useEffect(() => {
    if (!hasValidPayload || userId == null) return
    const prefix = `${userId}:`
    const current = new Set(
      announcements.map((announcement) =>
        getAnnouncementStorageKey(userId, announcement),
      ),
    )
    const stale = Object.keys(records).filter(
      (key) => key.startsWith(prefix) && !current.has(key),
    )
    if (stale.length === 0) return
    try {
      setRecords((previous) => {
        const next = { ...previous }
        stale.forEach((key) => delete next[key])
        return next
      })
    } catch {
      // The in-memory state is already updated when storage writes fail.
    }
  }, [announcements, hasValidPayload, userId, records, setRecords])

  const updateRecord = useCallback(
    (
      key: string,
      update: (record: AnnouncementRecord) => AnnouncementRecord,
    ) => {
      try {
        setRecords((previous) => ({
          ...previous,
          [key]: update(previous?.[key] ?? {}),
        }))
      } catch {
        // The in-memory state is already updated when storage writes fail.
      }
    },
    [setRecords],
  )

  /** Sends the event once per user and version, across reloads. */
  const trackOnce = useCallback(
    (view: AnnouncementView, event: AnnouncementEvent) => {
      const sessionKey = `${view.key}:${event}`
      if (
        trackedThisSession.current.has(sessionKey) ||
        records[view.key]?.tracked?.includes(event)
      ) {
        return
      }
      trackedThisSession.current.add(sessionKey)

      observe.track(AnalyticsNamespace.SHARED, ANALYTICS_EVENTS[event], {
        announcement_id: view.id,
        announcement_version: view.version,
        severity: view.severity,
      })
      updateRecord(view.key, (record) => ({
        ...record,
        tracked: [...(record.tracked ?? []), event],
      }))
    },
    [records, updateRecord],
  )

  const dismiss = useCallback(
    (view: AnnouncementView) => {
      if (view.dismissMode !== 'dismiss') return
      trackOnce(view, 'dismissed')
      updateRecord(view.key, (record) => ({ ...record, dismissed: true }))
    },
    [trackOnce, updateRecord],
  )

  const setCollapsed = useCallback(
    (view: AnnouncementView, collapsed: boolean) => {
      if (view.dismissMode !== 'collapse') return
      updateRecord(view.key, (record) => ({ ...record, collapsed }))
    },
    [updateRecord],
  )

  return { announcements: views, trackOnce, dismiss, setCollapsed }
}
